import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { useUserStore } from '../store/userStore';

/** Охват права, от узкого к широкому (как `SCOPE_ORDER` в `permissions/catalog.ts` бэкенда). */
export type PermissionScope = 'self' | 'reports' | 'subtree' | 'company';

/** Ответ `GET /users/me/permissions`: права текущего человека, посчитанные сервером. */
export interface MyPermissions {
  grants: Partial<Record<string, PermissionScope>>;
  isOwner: boolean;
  /** Права, открывающие и своё (на `self`, от владельца и «всех прав»). */
  own?: string[];
  hasReports: boolean;
  processes: Record<string, unknown>;
  /** Разделы, которыми человек пользуется: подключённые минус суженные до чужих ролей. */
  modules?: string[];
}

/** Право на всю компанию — как `can(p, perm)` без субъекта на бэкенде. */
export const hasCompanyGrant = (perms: MyPermissions | undefined, key: string) =>
  perms?.grants[key] === 'company';

/** Права текущего человека с сервера. Фронт их не вычисляет, а только читает. */
export const useMyPermissions = () => {
  const userId = useUserStore((s) => s.user?._id);
  return useQuery({
    // userId в ключе: после входа от имени или смены учётки права не берутся из кеша прошлого.
    queryKey: ['users', 'me', 'permissions', userId],
    queryFn: (): Promise<MyPermissions> => api('/users/me/permissions'),
    enabled: !!userId,
    staleTime: 60_000,
  });
};

/** Есть ли у текущего человека право `key` на всю компанию (меню, маршруты, глобальные кнопки). */
export const usePermission = (key: string): boolean =>
  hasCompanyGrant(useMyPermissions().data, key);

/**
 * Право хоть на кого-то, кроме себя: на всю компанию или на своих людей, если они есть
 * (как `canManageEssays` / `canManageQuarterly` на бэкенде). Страницы «Управление».
 */
export const hasGrantOnOthers = (perms: MyPermissions | undefined, key: string) => {
  const scope = perms?.grants[key];
  return (
    scope === 'company' || (!!perms?.hasReports && (scope === 'reports' || scope === 'subtree'))
  );
};
